import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";
import classnames from "classnames/dedupe";
import Axios from "axios";

import { getNestedObjValue } from "components/prime-data-table/helpers";
import { auditForeignKeysColumns } from "./expertise-audit";
import { useFetchTableData } from "hooks/useFetchTableData";
import { auditColumns } from "./audit-columns";
import { downloadFile } from "utils/file/download-file";
import { useDeepCompare } from "hooks/useDeepCompare";
import { handleRequest } from "utils/handleRequest";
import addToast from "utils/addToast";
import { GT } from "components/index";

import "./style.scss";
import { Filters } from "components/prime-data-table";

export const ModalAuditTable = (props) => {
    const { modalOpen } = props;

    return (
        <GT.StatelessModalWrapper modalOpen={modalOpen}>
            <AuditTable {...props} />
        </GT.StatelessModalWrapper>
    );
};

const AuditTable = (props) => {
    const { closeModal, auditData, instance, tableName, tableColumns, modalOpen, selectChoices } = props;
    const { model, appLabel } = auditData;

    const { t } = useTranslation();

    const historyTypeChoices = [
        { label: t("auditApp>>Change"), value: "~" },
        {
            label: t("auditApp>>Add"),
            value: "%2B",
        },
    ];
    const { data, handleReload } = useFetchTableData({
        url: "audit",
        // configParams: { params: { hideToast: true } },
        additionalParams: `&appLabel=${appLabel}&model=${model}&instanceId=${instance?.id}`,
        onError: (err: any) => {
            addToast({ title: "No audit events found", content: "or audit for this table is disabled", color: "danger" });
        },
    });

    const deepComparedData = useDeepCompare(data);

    const wrappedColumns = useMemo(() => {
        let foreignKeysColumns: unknown[] = [];
        if (!!(data as any)?.foreignKeys) {
            foreignKeysColumns = auditForeignKeysColumns({
                columns: [...tableColumns, ...auditColumns],
                foreignKeys: (data as any)?.foreignKeys,
            }) as unknown[];
        }

        let allColumns = [...tableColumns, ...foreignKeysColumns];

        if (model == "ResultLocus") {
            allColumns.push({
                header: "Final value",
                field: "finalValue",
                sortable: true,
                filterBody: Filters.text,
            });
        }

        if (model == "Maintenance") {
            allColumns.push({
                header: "PrimaryID",
                field: "primaryId",
                sortable: true,
                filterBody: Filters.text,
            });
        }

        return allColumns.map((column) => {
            return {
                ...column,
                body: (rowData) => {
                    const value = getNestedObjValue(rowData.row, rowData.fieldName.split("."));
                    const className = (() => {
                        const currentElement = document.getElementById(`${column.field}-${rowData.rowIndex}`);
                        const prevElement = document.getElementById(`${column.field}-${rowData.rowIndex + 1}`);

                        const prevValue = prevElement?.innerText;
                        const currentValue = currentElement?.innerText?.replace(t("Removed"), "");

                        if (!!currentValue && !prevValue) return "audit-added";
                        if (!currentValue && !!prevValue) return "audit-removed";
                        if (currentValue != prevValue) return "audit-modified";
                    })();

                    return (
                        <div
                            className={classnames(className, { "pl-5": !column?.body || className === "audit-removed" })}
                            id={`${column.field}-${rowData.rowIndex}`}
                        >
                            {(className === "audit-removed" && t("Removed")) || (column?.body && column.body(rowData)) || value}
                        </div>
                    );
                },
            };
        });
    }, [deepComparedData]);

    const handleExport = async () => {
        await handleRequest({
            request: () =>
                Axios.post("export-audit-to-xlsx", {
                    modelName: model,
                    appLabel,
                    instanceId: instance?.id,
                }),
            onSuccess: (response) => {
                downloadFile({ data: { file: response.data?.file.replace("media/", ""), name: `${appLabel}-${model}-audit` } });
            },
        });
    };

    const handleExportCSV = async () => {
        await handleRequest({
            request: () =>
                Axios.post("export-audit-to-csv", {
                    modelName: model,
                    appLabel,
                    instanceId: instance?.id,
                }),
            onSuccess: (response) => {
                downloadFile({ data: { file: response.data?.file.replace("media/", ""), name: `${appLabel}-${model}-audit` } });
            },
        });
    };

    const columns = [...auditColumns, ...wrappedColumns.map((col) => ({ ...col, width: 150 }))];

    return (
        <Modal className="audit-window" isOpen={modalOpen} toggle={closeModal} fade>
            <GT.ModalHeader closeModal={closeModal} name="Audit" />
            <ModalBody style={{ overflowY: "auto" }}>
                <GT.PrimeDataTable
                    tableName={`audit-table-${tableName}`}
                    notSelectFirst
                    onReload={handleReload}
                    dataSet={data?.data}
                    meta={data?.meta}
                    columns={columns}
                    idSelector="historyId"
                    adjustTableHeight={300}
                    getContainerHeight={false}
                    selectChoices={{ historyType: historyTypeChoices, ...selectChoices }}
                >
                    <GT.Button text="Export XLSX" onClick={handleExport} withoutPerm />
                    <GT.Button text="Export CSV" onClick={handleExportCSV} withoutPerm />
                </GT.PrimeDataTable>
            </ModalBody>
            <GT.ModalFooter closeModal={closeModal} cancelName="Close" />
        </Modal>
    );
};
